/**
 * Right-mouse free look: hold the secondary button and drag, and the pilot's
 * head turns off the airframe's forward axis; let go and it eases back to
 * centre (user request, 2026-08-25).
 *
 * This file is the whole decision half — clamps, sensitivity, the return
 * easing — as pure functions over an immutable state, on the same split
 * `pointer.ts` states for the mouse's other two controls: listeners in
 * `free-look-input.ts`, judgement here where a test can reach it.
 *
 * ## It is a camera, and nothing else
 *
 * Nothing this file computes ever reaches `FlightInputs`. That is structural
 * rather than a promise: `readFlightInputs` (`scene-inputs.ts`) takes a
 * `PointerFrame`, and a `PointerFrame` has two fields — `firing` and
 * `throttleDelta` — neither of which this module can produce or reach. A
 * free-look drag therefore cannot enter the sim, and so cannot enter the
 * Mission Recorder's tape (which stores the `FlightInputs` each frame was
 * flown with) or `/duel`'s lockstep wire (which sends the same). Turning your
 * head is not an input to an aeroplane, and the tape of a flight must replay
 * the same aeroplane however the pilot was looking while he flew it.
 * `free-look.test.ts` asserts both halves.
 *
 * ## Where the limits come from
 *
 * The port already has a calibrated answer to "how far can this pilot turn his
 * head": the six snap glances (`cockpit-view.ts`'s `GLANCE_YAW_DEG` /
 * `GLANCE_PITCH_DEG`), which are the original's own `F1`–`F6` views. Free look
 * is a continuum between them and must not see *more* than they imply is
 * possible, so both pitch limits are taken straight off the glance table — the
 * `up` glance's +35° and the `down` glance's −55°, imported rather than
 * restated so they cannot drift apart.
 *
 * Yaw is the one place free look is deliberately **tighter** than the table.
 * The `back` glance is a full 180° reversal, which is a *snap* view — the
 * original gives it its own key because it is a deliberate look over the
 * shoulder, not somewhere a head drifts. Letting a drag reach it would mean
 * looking straight backwards through the headrest, and would also make the
 * whole rear hemisphere reachable two ways with nothing to distinguish them.
 * So the drag stops at ±150°: past both side glances (±90°), far enough to
 * clear each rear quarter, and 30° short of the reversal that stays `R`'s.
 */

import type { CameraMode } from "./camera-modes.ts";
import { GLANCE_PITCH_DEG, LOOK_AHEAD, type LookAim } from "./cockpit-view.ts";
import type { GlanceDirection } from "./keys.ts";

/**
 * How far the view swings per CSS pixel of pointer travel.
 *
 * Chosen from the travel it implies rather than from feel: at 0.25°/px the
 * full ±150° of yaw is 600 px of drag, so a player on a 1280-px-wide window
 * reaches either yaw stop in roughly half a screen width and the two side
 * glances (±90°) sit 360 px out. Fast enough to check a rear quarter in one
 * flick of the wrist, slow enough that the middle 100 px — the range a hand
 * moves without meaning to — is under 25°.
 */
export const FREE_LOOK_DEG_PER_PX = 0.25;

/** ±150°: past the side glances, short of the `back` glance's reversal — see the module doc. */
export const FREE_LOOK_YAW_LIMIT_DEG = 150;

/** The `up` glance's own angle (`GLANCE_PITCH_DEG.up`) — free look sees no higher than a snap view does. */
export const FREE_LOOK_PITCH_MAX_DEG = GLANCE_PITCH_DEG.up;

/** The `down` glance's own angle (`GLANCE_PITCH_DEG.down`), negative — the over-the-side look, and no further. */
export const FREE_LOOK_PITCH_MIN_DEG = GLANCE_PITCH_DEG.down;

/**
 * How long the view takes to ease home after the button comes up.
 *
 * Short enough to feel like a head snapping forward rather than a camera being
 * animated, long enough that the horizon does not jump: at the ±150° yaw stop
 * this is a 600°/s peak rate, which is about what a real neck does and about
 * four frames' worth of travel at 60 Hz.
 */
export const FREE_LOOK_RETURN_SECONDS = 0.25;

/**
 * Slack on the clock the return is measured against, in seconds.
 *
 * `returnElapsedS` is a running sum of frame times, so it lands *near*
 * `FREE_LOOK_RETURN_SECONDS` and essentially never on it: fifteen frames of
 * `1/60` add up to 0.24999999999999997, which without this is one frame short
 * and leaves the view a ten-thousandth of a degree off centre — invisible, but
 * enough to keep `freeLookActive` true and the painted panel suppressed for an
 * extra frame with nothing on screen to explain it. A nanosecond of tolerance
 * absorbs the accumulation without ever absorbing a real frame.
 */
const RETURN_CLOCK_EPSILON_S = 1e-9;

/**
 * Cubic ease-out (`1 - (1 - t)³`) on the return, evaluated on the *elapsed
 * fraction* rather than as a per-frame decay.
 *
 * A decay (`aim *= k^dt`) never actually arrives, so the panel would stay
 * suppressed for ever and "is the pilot looking around" would have no answer;
 * this reaches exactly zero at `FREE_LOOK_RETURN_SECONDS` and stops. Ease-out
 * rather than ease-in-out because the motion starts from a head already in
 * motion — it should leave fast and settle, not creep out of the corner.
 */
export function freeLookReturnEasing(t: number): number {
  const clamped = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const remaining = 1 - clamped;
  return 1 - remaining * remaining * remaining;
}

/**
 * One drag's worth of state. Immutable: every function below returns the next
 * one, so a test can hold two generations side by side and the input layer
 * (`free-look-input.ts`) owns the single mutable reference.
 */
export interface FreeLookState {
  readonly aim: LookAim;
  /** Whether the secondary button is down right now. */
  readonly dragging: boolean;
  /**
   * The aim at the instant the button came up. The return interpolates from a
   * *fixed* start rather than from wherever it has got to, which is what makes
   * `FREE_LOOK_RETURN_SECONDS` the honest duration of the whole return instead
   * of a half-life.
   */
  readonly returnFrom: LookAim;
  readonly returnElapsedS: number;
}

/** Dead ahead, nothing held, nothing returning. */
export const FREE_LOOK_AT_REST: FreeLookState = {
  aim: LOOK_AHEAD,
  dragging: false,
  returnFrom: LOOK_AHEAD,
  returnElapsedS: 0,
};

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

/** Whether the view is anywhere but dead ahead. */
export function aimIsOffCentre(aim: LookAim): boolean {
  return aim.yawDeg !== 0 || aim.pitchDeg !== 0;
}

/**
 * Whether the pilot is looking around this frame — held, or still easing home.
 *
 * True from the instant the button goes down, before the pointer has moved at
 * all. Suppressing the painted panel on the *press* rather than on the first
 * degree of travel is what stops it flickering back on every time a drag
 * happens to cross the centre; see `FlightSurface.tsx` for what reads this.
 */
export function freeLookActive(state: FreeLookState): boolean {
  return state.dragging || aimIsOffCentre(state.aim);
}

/**
 * The button went down. The aim is kept exactly where it is, which is the
 * "instant abort of the return" half of the contract: press again mid-return
 * and the view stops dead at whatever angle it had eased back to, rather than
 * jumping to centre or finishing the animation first.
 */
export function freeLookPress(state: FreeLookState): FreeLookState {
  return { aim: state.aim, dragging: true, returnFrom: state.aim, returnElapsedS: 0 };
}

/**
 * Pointer travel, in CSS pixels, while the button is held. Ignored when it is
 * not: a move event that arrives after the release (or from a second pointer)
 * must not steer a view that is already on its way home.
 *
 * Rightward travel yaws right and downward travel pitches down, matching the
 * signs the glance table uses (`GLANCE_YAW_DEG.right` is +90,
 * `GLANCE_PITCH_DEG.down` is −55) — so the mouse moves the *view*, not a
 * camera the view is painted on.
 */
export function freeLookDrag(state: FreeLookState, dxPx: number, dyPx: number): FreeLookState {
  if (!state.dragging) return state;
  return {
    ...state,
    aim: {
      yawDeg: clamp(state.aim.yawDeg + dxPx * FREE_LOOK_DEG_PER_PX, -FREE_LOOK_YAW_LIMIT_DEG, FREE_LOOK_YAW_LIMIT_DEG),
      pitchDeg: clamp(state.aim.pitchDeg - dyPx * FREE_LOOK_DEG_PER_PX, FREE_LOOK_PITCH_MIN_DEG, FREE_LOOK_PITCH_MAX_DEG),
    },
  };
}

/** The button came up (or the pointer was cancelled, or the window lost focus) — the return starts here. */
export function freeLookRelease(state: FreeLookState): FreeLookState {
  if (!state.dragging) return state;
  if (!aimIsOffCentre(state.aim)) return FREE_LOOK_AT_REST;
  return { aim: state.aim, dragging: false, returnFrom: state.aim, returnElapsedS: 0 };
}

/**
 * One frame of the eased return. A no-op while the button is held and once the
 * view is home, so the flight loop can call it unconditionally.
 */
export function freeLookAdvance(state: FreeLookState, dtSeconds: number): FreeLookState {
  if (state.dragging || !aimIsOffCentre(state.aim)) return state;
  const elapsed = state.returnElapsedS + Math.max(dtSeconds, 0);
  if (elapsed >= FREE_LOOK_RETURN_SECONDS - RETURN_CLOCK_EPSILON_S) return FREE_LOOK_AT_REST;
  const remaining = 1 - freeLookReturnEasing(elapsed / FREE_LOOK_RETURN_SECONDS);
  return {
    ...state,
    aim: { yawDeg: state.returnFrom.yawDeg * remaining, pitchDeg: state.returnFrom.pitchDeg * remaining },
    returnElapsedS: elapsed,
  };
}

/** What the flight loop reads: where the head is pointed, and whether it is off the airframe's own axis at all. */
export interface FreeLookFrame {
  readonly aim: LookAim;
  readonly active: boolean;
}

/** Dead ahead — what a route with no mouse (or a scene between frames) reports. */
export const NO_FREE_LOOK: FreeLookFrame = { aim: LOOK_AHEAD, active: false };

export function freeLookFrameOf(state: FreeLookState): FreeLookFrame {
  return { aim: state.aim, active: freeLookActive(state) };
}

/**
 * Precedence between the two ways of looking around, decided once and here.
 *
 * A live free look **beats** the snap glances rather than adding to them. They
 * are the same degree of freedom, and adding a ±150° drag to the `back`
 * glance's 180° would take the view a long way past what the airframe's own
 * calibration says a neck can do — the very limit this module exists to
 * respect. Replacing is also the rule with one number in it instead of two: the
 * view is either at a snap angle or at the drag's, never at a sum nobody
 * specified.
 *
 * "Live" includes the eased return, so the glance stays `forward` until the
 * head is actually home and a `Q` held throughout resumes as the view arrives,
 * rather than snapping 90° mid-flight.
 *
 * Resolving it here rather than at the camera is what keeps the frame
 * consistent: `scene-loop.ts` calls this once and everything downstream — the
 * camera, the painted panel, the hit overlays, the dash quad — reads the same
 * answer, so there is no glance the camera is quietly ignoring.
 */
export function glanceUnderFreeLook(freeLook: FreeLookFrame, snapGlance: GlanceDirection): GlanceDirection {
  return freeLook.active ? "forward" : snapGlance;
}

/**
 * Whether `FlightSurface.tsx` mounts the cockpit panel this frame — the one
 * thing free look changes about the picture outside the camera.
 *
 * The painted cockpit is a **fixed forward view**. Every glance the art has is
 * a whole separate composition baked from the aircraft's own `.TTM`
 * (`cockpit-frame.ts`), so there is no picture at all for a head held at some
 * angle between them, and inventing one — leaving the forward dashboard up
 * while the camera yaws — is the exact bug the user reported on 2026-08-19 as
 * "Q/E tilts sideways irrespective of the cockpit". The port already has an
 * answer for a view with no art: the `up` glance shows no panel. Free look
 * takes the same one.
 *
 * **3D COCKPIT is the exception, and needs no rule.** There the dashboard is
 * geometry in the scene rather than an overlay on it, so a head that turns away
 * from it sees it leave the frame by itself. The panel stays active — it is
 * still composing the texture the quad draws (`scene-cockpit-3d.ts`), and
 * standing it down would blank the dashboard rather than let the pilot look
 * past it.
 */
export function cockpitPanelActive(cameraMode: CameraMode, freeLook: boolean, cockpit3d: boolean): boolean {
  if (cameraMode !== "cockpit") return false;
  return cockpit3d || !freeLook;
}
